<template>
  <ReportWrapper>
    <template #actions>
      <div class="flex gap-x-2">
        <v-menu :close-on-content-click="false" nudge-right="10" offset-x :max-width="300">
          <template #activator="{ on, attrs }">
            <div class="relative" v-bind="attrs" v-on="on">
              <CfsTextField
                placeholder="Period"
                label=""
                hide-details
                :value="periodDisplayValue"
              />
              <div v-if="selectedPeriod" class="top-0 absolute z-100 w-full h-full cursor-pointer">
                <v-icon
                  class="absolute top-[8px] right-[-160px] cursor-pointer"
                  color="waterloo"
                  @click.stop="onChangePeriod()"
                >
                  mdi-close
                </v-icon>
              </div>
            </div>
          </template>
          <v-date-picker
            :value="periodDisplayValue"
            color="primary"
            type="month"
            @change="onChangePeriod"
          />
        </v-menu>
      </div>

      <div class="flex gap-x-2 grow-0">
        <v-btn
          color="primary"
          :disabled="
            !selectedCustomers.length ||
            selectedCustomers?.every((customer) => customer.status?.code === 'finalized')
          "
          :loading="isFinalizingReport"
          @click="onFinalize"
        >
          Finalize
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!selectedCustomers.length"
          :loading="isDownloadingReport"
          @click="onDownloadReport"
        >
          <v-icon size="1rem" class="mr-1">mdi-tray-arrow-down</v-icon>
          <span>Download</span>
        </v-btn>
      </div>
    </template>
    <CfsTable
      class="charge-table"
      :headers="WarehouseReportTableHeaders"
      v-bind="$attrs"
      :items="customers"
      :loading="isFetchingCustomers"
      :value="selectedCustomers"
      :show-select="true"
      item-key="customer.accountNumber"
      hide-default-footer
      :items-per-page="-1"
      tr-background
      @input="onSelectCustomers"
    >
      <template #item.data-table-select="{ isSelected, select }">
        <v-simple-checkbox :value="isSelected" color="primary" @input="select" />
      </template>

      <template #header.data-table-select="{ props, on }">
        <v-simple-checkbox v-bind="props" color="primary" v-on="on" />
      </template>
    </CfsTable>
  </ReportWrapper>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useReports } from '@/composables/reports/useReports';
import { downloadFile } from '@/helpers/download';
import { AxiosError } from 'axios';
import { useToast } from '@/composables/useToast';
import { useState } from '@/composables/common/useState';
import ReportWrapper from '@/components/reports/ReportWrapper.vue';
import { DatePeriod } from '@/types/common/dateRange.model';
import { ReportCustomerEntity } from '@/services/reports/reports.customer.service';
import { useReportCustomers } from '@/composables/reports/useReportCustomers';
import { DataTableHeader } from 'vuetify';

export const WarehouseReportTableHeaders: DataTableHeader[] = [
  { text: 'Account Number', value: 'customer.accountNumber' },
  { text: 'Account Name', value: 'customer.accountName' },
  { text: 'Finalization Status', value: 'status.description', sortable: false },
  { text: '', value: 'view', sortable: false },
];

export default defineComponent({
  name: 'WarehouseReport',
  components: {
    ReportWrapper,
  },
  setup() {
    const toast = useToast();
    const [selectedCustomers, setCustomers] = useState<ReportCustomerEntity[]>([]);
    const [selectedPeriod, setPeriod] = useState<DatePeriod | null>(null);

    const periodDisplayValue = computed(() =>
      selectedPeriod.value ? `${selectedPeriod.value.year}-${selectedPeriod.value.month}` : ''
    );

    const { useGetWarehouseCustomers } = useReportCustomers();
    const { useDownloadWarehouseReport, useFinalizeWarehouse } = useReports();

    const {
      data: customers,
      isFetching: isFetchingCustomers,
      isSuccess: hasFetchedCustomers,
      refetch: refetchCustomers,
    } = useGetWarehouseCustomers(selectedPeriod);

    const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
      useDownloadWarehouseReport();
    const { mutateAsync: finalizeReport, isLoading: isFinalizingReport } = useFinalizeWarehouse();

    const onChangePeriod = (period?: string) => {
      if (!period) return setPeriod(null);
      const [year, month] = period?.split('-') ?? '';
      setPeriod({ year, month });
      setCustomers([]);
    };

    const onDownloadReport = async () => {
      if (isDownloadingReport.value) return;
      try {
        const data = await downloadReport({
          customer:
            selectedCustomers.value.length === customers.value?.length
              ? 'all'
              : selectedCustomers.value.map((customer) => customer.customer.accountNumber),
          period: selectedPeriod.value as DatePeriod,
        });
        if (!data) return;

        downloadFile(data.data, data.filename ?? 'Warehouse Collect Report');
      } catch (error) {
        const axiosError = error as AxiosError<string>;
        toast.error(axiosError?.response?.data!);
      }
    };

    const onFinalize = async () => {
      if (isFinalizingReport.value) return;
      await finalizeReport({
        customer:
          selectedCustomers.value.length === customers.value?.length
            ? 'all'
            : selectedCustomers.value.map((customer) => customer.customer.accountNumber),
        isPending: selectedCustomers.value.some((customer) => customer.status?.code === 'pending'),
        period: selectedPeriod.value as DatePeriod,
      });

      await refetchCustomers();
    };

    const onSelectCustomers = (customers: ReportCustomerEntity[]) => {
      setCustomers(customers);
    };

    return {
      selectedCustomers,
      selectedPeriod,
      periodDisplayValue,

      customers,
      hasFetchedCustomers,
      isFetchingCustomers,

      isFinalizingReport,
      isDownloadingReport,

      onChangePeriod,
      onFinalize,
      onDownloadReport,
      onSelectCustomers,

      WarehouseReportTableHeaders,
    };
  },
});
</script>
